import {
  findLocationEntryWithParents,
  HierarchyNodeMetadata,
  HierarchyNodeType
} from '@domain/shared'
import {PlantHierarchyNode, PlantHierarchyType} from '@hproduce/suite-core'

export type KpiLocationResolution =
  /** the Suite node maps to the global KPI view */
  | {type: 'global'}
  | {type: 'location'; locationId: string}
  /** none of the node's plants are part of the KPI hierarchy */
  | {type: 'notFound'}

export const findSuiteHierarchyNode = (
  nodes: PlantHierarchyNode[],
  hierarchyId: string
): PlantHierarchyNode | undefined => {
  for (const node of nodes) {
    if (node.hierarchyId === hierarchyId) return node
    const match = findSuiteHierarchyNode(node.children ?? [], hierarchyId)
    if (match) return match
  }
  return undefined
}

export const collectSuitePlantHierarchyIds = (node: PlantHierarchyNode): string[] =>
  node.type === PlantHierarchyType.Plant
    ? [node.hierarchyId]
    : (node.children ?? []).flatMap(collectSuitePlantHierarchyIds)

/** plantId → ids of its KPI ancestors, ordered from the global root down to its direct parent */
const collectKpiPlantAncestors = (
  node: HierarchyNodeMetadata,
  ancestors: string[] = [],
  result = new Map<string, string[]>()
): Map<string, string[]> => {
  if (node.type === HierarchyNodeType.Plant) {
    result.set(node.id, ancestors)
    return result
  }
  node.children.forEach((child) => collectKpiPlantAncestors(child, [...ancestors, node.id], result))
  return result
}

const commonPrefix = (lists: string[][]): string[] =>
  lists.reduce((prefix, list) => {
    const length = prefix.findIndex((id, index) => list[index] !== id)
    return length === -1 ? prefix : prefix.slice(0, length)
  })

/**
 * Suite (UPM) and KPI dashboard hierarchies share no ids and differ in structure
 * (e.g. Suite "US" is split into several KPI countries below region "r_US"), but both contain
 * the same plants. A Suite node therefore maps to the lowest KPI node containing all of its plants.
 */
export const resolveKpiLocation = (
  kpiHierarchy: HierarchyNodeMetadata,
  plantIds: string[]
): KpiLocationResolution => {
  const plantAncestors = collectKpiPlantAncestors(kpiHierarchy)
  const ancestorLists = plantIds
    .map((plantId) => plantAncestors.get(plantId))
    .filter((ancestors): ancestors is string[] => !!ancestors)

  if (ancestorLists.length === 0) return {type: 'notFound'}

  const lowestCommonAncestor = commonPrefix(ancestorLists).at(-1)

  return !lowestCommonAncestor || lowestCommonAncestor === kpiHierarchy.id
    ? {type: 'global'}
    : {type: 'location', locationId: lowestCommonAncestor}
}

/**
 * KPI locations a URL is made of: the area, followed by the location itself when it is below the
 * area (region or country), and area/country/plant for plants. Regions above a country are skipped
 * so Suite's breadcrumb reads "Area | Country" and "Area | Country | Plant".
 */
export const getKpiLocationPath = (
  kpiHierarchy: HierarchyNodeMetadata,
  locationId: string
): HierarchyNodeMetadata[] => {
  const chain = findLocationEntryWithParents(kpiHierarchy, locationId) ?? []
  const location = chain.at(-1)

  if (!location || location.type === HierarchyNodeType.Global) return []

  const area = chain.find(({type}) => type === HierarchyNodeType.Area)
  const country = chain.find(({type}) => type === HierarchyNodeType.Country)
  const path =
    location.type === HierarchyNodeType.Plant ? [area, country, location] : [area, location]

  return path.filter(
    (node, index): node is HierarchyNodeMetadata => !!node && path.indexOf(node) === index
  )
}

/** Suite levels that map to no KPI location below their own level */
const suiteNodeKpiLevels: Partial<Record<PlantHierarchyType, HierarchyNodeType>> = {
  [PlantHierarchyType.Global]: HierarchyNodeType.Global,
  [PlantHierarchyType.ManagementArea]: HierarchyNodeType.Area
}

/**
 * KPI location of a Suite (UPM) node: the lowest KPI node containing its plants
 * (`resolveKpiLocation`), but never below the node's own level. Few of an area's plants may have
 * KPI data, all in one country, and the area must still map to the KPI area rather than that
 * country. `getPlantId` maps a Suite plant's hierarchyId to its plantId.
 */
export const resolveSuiteNodeKpiLocation = (
  kpiHierarchy: HierarchyNodeMetadata,
  node: PlantHierarchyNode,
  getPlantId: (hierarchyId: string) => string | undefined
): KpiLocationResolution => {
  const plantIds = collectSuitePlantHierarchyIds(node)
    .map(getPlantId)
    .filter((plantId): plantId is string => !!plantId)
  const resolution = resolveKpiLocation(kpiHierarchy, plantIds)
  const level = suiteNodeKpiLevels[node.type]

  if (resolution.type !== 'location' || !level) return resolution
  if (level === HierarchyNodeType.Global) return {type: 'global'}

  const levelLocation = (
    findLocationEntryWithParents(kpiHierarchy, resolution.locationId) ?? []
  ).find(({type}) => type === level)
  return levelLocation ? {type: 'location', locationId: levelLocation.id} : resolution
}

/**
 * The reverse of `resolveSuiteNodeKpiLocation`: the deepest Suite (UPM) node mapping to the given
 * KPI location.
 */
export const findSuiteNodeForKpiLocation = (
  nodes: PlantHierarchyNode[],
  kpiHierarchy: HierarchyNodeMetadata,
  getPlantId: (hierarchyId: string) => string | undefined,
  locationId: string
): PlantHierarchyNode | undefined => {
  for (const node of nodes) {
    if (node.type === PlantHierarchyType.Plant) continue

    const match = findSuiteNodeForKpiLocation(
      node.children ?? [],
      kpiHierarchy,
      getPlantId,
      locationId
    )
    if (match) return match

    const resolution = resolveSuiteNodeKpiLocation(kpiHierarchy, node, getPlantId)
    if (resolution.type === 'location' && resolution.locationId === locationId) return node
  }
  return undefined
}

/**
 * Whether a KPI location shows part of a Suite (UPM) node: the node's own location or one below it.
 * Plants contain no location overviews; nodes spanning several areas contain all of them.
 */
export const isKpiLocationWithinSuiteNode = (
  node: PlantHierarchyNode,
  kpiHierarchy: HierarchyNodeMetadata,
  getPlantId: (hierarchyId: string) => string | undefined,
  locationId: string
): boolean => {
  if (node.type === PlantHierarchyType.Plant) return false

  const resolution = resolveSuiteNodeKpiLocation(kpiHierarchy, node, getPlantId)

  if (resolution.type !== 'location') return resolution.type === 'global'

  return (findLocationEntryWithParents(kpiHierarchy, locationId) ?? []).some(
    ({id}) => id === resolution.locationId
  )
}
